import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import React, { type ComponentType, type FunctionComponent, useState } from 'react';

import { FieldTypeMenuItem } from './AddFieldMenuItem';
import type { AddFieldMenuSelection, FieldTypeEntry } from './types';

const advancedRowStyles = xcss({
  width: '100%',
  borderRadius: 'radius.small',
  paddingInline: 'space.050',
  paddingBlock: 'space.075',
  color: 'color.text',
  backgroundColor: 'color.background.neutral.subtle',
  ':hover': {
    backgroundColor: 'color.background.neutral.subtle.hovered'
  }
});

const advancedItemsStyles = xcss({
  paddingBlockStart: 'space.025'
});

const itemGridStyles = xcss({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  columnGap: 'space.100',
  rowGap: 'space.025'
});

const productIconWrapperStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center'
});

export const AdvancedFieldTypeSection: FunctionComponent<{
  label: string;
  Icon: ComponentType<{
    appearance?: 'brand';
    label: string;
    size?: 'small';
  }>;
  items: FieldTypeEntry[];
  isExpandedByDefault?: boolean;
  isExpandedBySearch: boolean;
  onSelect?: (selection: AddFieldMenuSelection) => void;
}> = ({
  label,
  Icon,
  items,
  isExpandedByDefault = false,
  isExpandedBySearch,
  onSelect
}) => {
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const shouldShowItems = isExpanded || isExpandedBySearch;

  return (
    <Stack space="space.025">
      <Pressable
        xcss={advancedRowStyles}
        onClick={() => setIsExpanded((value) => !value)}
        testId={`add-field-menu-${label.toLocaleLowerCase().replaceAll(' ', '-')}`}
      >
        <Inline spread="space-between" alignBlock="center">
          <Inline space="space.100" alignBlock="center" shouldWrap={false}>
            <Box xcss={productIconWrapperStyles}>
              <Icon appearance="brand" size="small" label="" />
            </Box>
            <Text>{label}</Text>
          </Inline>
          {shouldShowItems ? (
            <ChevronUpIcon label="" size="small" />
          ) : (
            <ChevronRightIcon label="" size="small" />
          )}
        </Inline>
      </Pressable>
      {shouldShowItems && (
        <Box xcss={advancedItemsStyles}>
          <Box xcss={itemGridStyles}>
            {items.map((fieldType) => (
              <FieldTypeMenuItem
                key={fieldType.type}
                fieldType={fieldType}
                onSelect={onSelect}
              />
            ))}
          </Box>
        </Box>
      )}
    </Stack>
  );
};
